import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Trash2, Plus, BookOpen } from 'lucide-react';
import type { LineaPresupuesto, ProductoServicio } from '@/lib/types';
import { lineaVacia, formatEUR } from '@/lib/calc';
import { CatalogoSelector } from '@/components/CatalogoSelector';
import { useProductos } from '@/hooks/useData';

interface Props {
  lineas: LineaPresupuesto[];
  onChange: (lineas: LineaPresupuesto[]) => void;
}

export function LineasEditor({ lineas, onChange }: Props) {
  const { productos } = useProductos();
  const [catalogoOpen, setCatalogoOpen] = useState(false);

  const update = (id: string, field: keyof LineaPresupuesto, value: string | number) => {
    onChange(lineas.map(l => l.id === id ? { ...l, [field]: value } : l));
  };
  const remove = (id: string) => onChange(lineas.filter(l => l.id !== id));
  const add = () => onChange([...lineas, lineaVacia()]);

  const addFromCatalogo = (p: ProductoServicio) => {
    const nueva: LineaPresupuesto = {
      id: crypto.randomUUID(),
      descripcion: [p.nombre, p.descripcion].filter(Boolean).join(' — '),
      unidades: 1,
      precioUnitario: p.precioBase,
      descuento: p.descuento,
      iva: p.iva,
    };
    onChange([...lineas, nueva]);
  };

  const lineTotal = (l: LineaPresupuesto) => {
    const bruto = l.unidades * l.precioUnitario;
    const neto = bruto * (1 - l.descuento / 100);
    return neto * (1 + l.iva / 100);
  };

  return (
    <div className="space-y-2">
      {/* Cabecera columnas */}
      <div className="hidden md:grid md:grid-cols-[2fr_80px_110px_80px_80px_100px_40px] gap-2 px-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
        <span>Descripción</span>
        <span className="text-right">Uds.</span>
        <span className="text-right">P. unit.</span>
        <span className="text-right">Dto.%</span>
        <span className="text-right">IVA%</span>
        <span className="text-right">Total</span>
        <span />
      </div>

      <AnimatePresence>
        {lineas.map((l) => (
          <motion.div
            key={l.id} layout
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            className="grid grid-cols-1 md:grid-cols-[2fr_80px_110px_80px_80px_100px_40px] gap-2 items-start bg-card border border-border rounded-lg p-3 md:p-2 md:items-center"
          >
            <div>
              <span className="text-xs text-muted-foreground md:hidden mb-1 block">Descripción</span>
              <Textarea value={l.descripcion} onChange={e => update(l.id, 'descripcion', e.target.value)} placeholder="Descripción del trabajo o material…" rows={2} className="text-sm resize-none min-h-0" />
            </div>
            <div>
              <span className="text-xs text-muted-foreground md:hidden mb-1 block">Unidades</span>
              <Input type="number" min="0" step="0.01" value={l.unidades} onChange={e => update(l.id, 'unidades', parseFloat(e.target.value) || 0)} className="text-right font-mono text-sm" />
            </div>
            <div>
              <span className="text-xs text-muted-foreground md:hidden mb-1 block">Precio unitario (€)</span>
              <Input type="number" min="0" step="0.01" value={l.precioUnitario} onChange={e => update(l.id, 'precioUnitario', parseFloat(e.target.value) || 0)} className="text-right font-mono text-sm" />
            </div>
            <div>
              <span className="text-xs text-muted-foreground md:hidden mb-1 block">Descuento %</span>
              <Input type="number" min="0" max="100" step="1" value={l.descuento} onChange={e => update(l.id, 'descuento', parseFloat(e.target.value) || 0)} className="text-right font-mono text-sm" />
            </div>
            <div>
              <span className="text-xs text-muted-foreground md:hidden mb-1 block">IVA %</span>
              <Select value={String(l.iva)} onValueChange={v => update(l.id, 'iva', Number(v))}>
                <SelectTrigger className="text-sm font-mono"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0%</SelectItem>
                  <SelectItem value="4">4%</SelectItem>
                  <SelectItem value="10">10%</SelectItem>
                  <SelectItem value="21">21%</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="text-right">
              <span className="text-xs text-muted-foreground md:hidden mb-1 block">Total</span>
              <span className="font-mono font-semibold text-sm text-foreground">{formatEUR(lineTotal(l))}</span>
            </div>
            <div className="flex justify-end md:justify-center">
              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => remove(l.id)}>
                <Trash2 size={15} />
              </Button>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Botones añadir */}
      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={add} className="flex-1 gap-2 border-dashed text-muted-foreground hover:text-foreground">
          <Plus size={16} /> Añadir línea manual
        </Button>
        <Button type="button" variant="outline" onClick={() => setCatalogoOpen(true)} className="flex-1 gap-2 border-dashed text-muted-foreground hover:text-foreground hover:border-primary/50">
          <BookOpen size={16} /> Añadir desde catálogo
        </Button>
      </div>

      <CatalogoSelector open={catalogoOpen} onClose={() => setCatalogoOpen(false)} productos={productos} onSelect={addFromCatalogo} />
    </div>
  );
}
